import React, { FC, useMemo, useState } from 'react';

import IconButton from 'components/buttons/iconButton';
import AampeToggleButton, {
    ToggleButtonProps,
} from 'components/shared/AampeToggleButton/AampeToggleButton';
import RenderHTML from 'components/shared/RenderHTML';
import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';
import { FormulaType } from '../../../constants/formulaType';
import ICONS from '../../../constants/icons';

import {
    defaultEditorDetails,
    EditorContext,
} from '../../../context/EditorContext';

import useQueryEmailTemplate from '../../../hooks/EmailTemplate/useQueryEmailTemplate';
import useCustomEmailTemplateDetail from '../../../hooks/custom-email-templates/useCustomEmailTemplateDetail';
import useFormulaQuery from '../../../hooks/formulas/useFormulaQuery';
import useMessageGenerationQuery from '../../../hooks/formulas/useMessageGenerationQuery';

import { EmailPreviewModel, SectionType } from '../../../models/editor.model';
import {
    FormulaDetailModel,
    FormulaPreviewCount,
    PreviewView,
} from '../../../models/formula.model';

import PhoneView from '../../formulaCreate/messagePreview/PhoneView';
import CoverMain from '../../formulaCreate/messagePreview/cover/CoverMain';
import { updateTemplateForPreview } from '../../formulaCreate/messagePreview/email/email.helper';

interface PreviewCombinationsPanelProps {
    formula: FormulaDetailModel;
}

interface EmailField {
    [key: string]: string;
}

const PreviewCombinationsPanel: FC<PreviewCombinationsPanelProps> = ({
    formula: formulaProp,
}) => {
    const [currentPreviewIndex, setCurrentPreviewIndex] = useState<number>(0);
    const [isMobileView, setIsMobileView] = useState<boolean>(false);

    const { data: formulaDetails } = useFormulaQuery(formulaProp.id, {});
    const formula = formulaDetails ?? formulaProp;

    const { data: previews } = useMessageGenerationQuery(
        formulaProp.id,
        FormulaPreviewCount.max,
    );

    const isEmailMessage = formula.message_type === FormulaType.email;
    const hasCoverPanel =
        formula.message_type === FormulaType.mobilePush ||
        formula.message_type === FormulaType.sms;

    // Email-specific logic
    const emailTemplateId = useMemo<string | undefined>(() => {
        return formula?.additional_metadata?.email_template_id;
    }, [formula]);

    const isAampeCustomEmailTemplate = useMemo<boolean>(() => {
        return !!formula?.additional_metadata?.is_aampe_custom_template;
    }, [formula]);

    const { data: templateData } = useQueryEmailTemplate(
        emailTemplateId as string,
        formula.message_type as FormulaType,
        isEmailMessage && !isAampeCustomEmailTemplate,
    );

    const { data: customTemplateData } = useCustomEmailTemplateDetail(
        emailTemplateId as string,
        isEmailMessage && isAampeCustomEmailTemplate,
    );

    const htmlContent = useMemo<string>(() => {
        if (isAampeCustomEmailTemplate) {
            return customTemplateData?.html_content ?? '';
        }
        return templateData?.htmlContent ?? '';
    }, [customTemplateData, isAampeCustomEmailTemplate, templateData]);

    const dynamicEmailFields = useMemo<string[]>(() => {
        if (isAampeCustomEmailTemplate) {
            return customTemplateData?.additional_metadata?.dynamic_keys ?? [];
        }
        return templateData?.dynamicFieldsWithModifiers as string[];
    }, [customTemplateData, isAampeCustomEmailTemplate, templateData]);

    const currentPreview = useMemo(() => {
        return (previews as EmailPreviewModel[])?.[currentPreviewIndex]
            ?.content;
    }, [previews, currentPreviewIndex]);

    const emailFieldsWithData = useMemo<EmailField>(() => {
        if (currentPreview) return currentPreview;
        if (dynamicEmailFields) {
            return Object.fromEntries(
                dynamicEmailFields.map((entry) => [entry, '']),
            );
        }
        return {} as EmailField;
    }, [currentPreview, dynamicEmailFields]);

    const updatedTemplate = useMemo<string>(() => {
        return updateTemplateForPreview(htmlContent, emailFieldsWithData);
    }, [emailFieldsWithData, htmlContent]);

    const handleLeftClick = () => {
        if (currentPreviewIndex > 0) setCurrentPreviewIndex((prev) => prev - 1);
    };

    const handleRightClick = () => {
        if (previews && currentPreviewIndex < previews.length - 1)
            setCurrentPreviewIndex((prev) => prev + 1);
    };

    // Non-email preview context
    const editorContextValue = useMemo(
        () => ({
            editorDetails: {
                ...defaultEditorDetails,
                formula: formula as FormulaDetailModel,
                formulaType: formula.message_type as FormulaType,
                previews: previews ?? [],
            },
            setEditorDetails: () => {},
        }),
        [formula, previews],
    );

    const toggleButtons = useMemo<ToggleButtonProps[]>(
        () =>
            [
                {
                    title: 'Desktop',
                    isSelected: isMobileView
                        ? PreviewView.MOBILE
                        : PreviewView.DESKTOP,
                    onChange: () => setIsMobileView(false),
                    value: PreviewView.DESKTOP,
                    isLeft: true,
                },
                {
                    title: 'Mobile',
                    isSelected: isMobileView
                        ? PreviewView.DESKTOP
                        : PreviewView.MOBILE,
                    onChange: () => setIsMobileView(true),
                    value: PreviewView.MOBILE,
                    isRight: true,
                },
            ] as ToggleButtonProps[],
        [isMobileView],
    );

    return (
        <Container>
            <TopControls>
                {isEmailMessage && (
                    <StyledToggleButtons
                        isMobileView={isMobileView}
                        buttons={toggleButtons}
                    />
                )}
            </TopControls>
            <Background>
                {isEmailMessage ? (
                    <>
                        {previews && previews.length > 0 && (
                            <PaginationContainer>
                                <IconButton
                                    Icon={ICONS.leftChevron}
                                    action={handleLeftClick}
                                    disabled={currentPreviewIndex === 0}
                                />
                                <PaginationText>
                                    {currentPreviewIndex + 1}/{previews.length}
                                </PaginationText>
                                <IconButton
                                    Icon={ICONS.rightChevron}
                                    action={handleRightClick}
                                    disabled={
                                        currentPreviewIndex ===
                                        previews.length - 1
                                    }
                                    color={aampeTheme.colors.solidA01}
                                />
                            </PaginationContainer>
                        )}
                        <PreviewContent>
                            <ScreenSizeContainer isMobile={isMobileView}>
                                {Object.keys(emailFieldsWithData).includes(
                                    SectionType.subject,
                                ) && (
                                    <SubjectLine>
                                        Subject:{' '}
                                        {
                                            emailFieldsWithData[
                                                SectionType.subject
                                            ]
                                        }
                                    </SubjectLine>
                                )}
                                <RenderHTML
                                    htmlString={updatedTemplate ?? ''}
                                />
                            </ScreenSizeContainer>
                        </PreviewContent>
                    </>
                ) : (
                    <EditorContext.Provider value={editorContextValue}>
                        {hasCoverPanel ? (
                            <CoverWrapper>
                                <CoverMain formulaId={formulaProp.id} />
                            </CoverWrapper>
                        ) : (
                            <PhoneView isCoverPreview />
                        )}
                    </EditorContext.Provider>
                )}
            </Background>
        </Container>
    );
};
export default PreviewCombinationsPanel;

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
});

const TopControls = styled.div({
    display: 'flex',
    justifyContent: 'center',
    padding: aampeTheme.spacing4(4),
    backgroundColor: aampeTheme.colors.white,
});

const CoverWrapper = styled.div({
    flex: 1,
    overflow: 'auto',
    '& .columns': {
        maxHeight: 'none',
    },
    '& > div > div:first-child': {
        position: 'sticky',
        top: 0,
        zIndex: 12,
    },
});

const Background = styled.div({
    flex: 1,
    backgroundColor: aampeTheme.colors.gray50,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
});

interface StyledToggleProps {
    isMobileView: boolean;
}

const StyledToggleButtons = styled(AampeToggleButton).withConfig({
    shouldForwardProp: (prop) => prop !== 'isMobileView',
})<StyledToggleProps>(({ isMobileView }) => ({
    backgroundColor: aampeTheme.colors.backgroundWG02,
    borderRadius: aampeTheme.borderRadius.default,
    padding: '2px',
    gap: '4px',
    '& > :first-child': {
        border: 'none',
        backgroundColor: !isMobileView
            ? aampeTheme.colors.white
            : 'transparent',
        color: !isMobileView
            ? aampeTheme.colors.solidA01
            : aampeTheme.colors.solidWG02,
        borderRadius: aampeTheme.borderRadius.default,
        '& > div': {
            fontWeight: aampeTheme.weights.medium,
        },
    },
    '& > :last-child': {
        border: 'none',
        backgroundColor: isMobileView ? aampeTheme.colors.white : 'transparent',
        color: isMobileView
            ? aampeTheme.colors.solidA01
            : aampeTheme.colors.solidWG02,
        borderRadius: aampeTheme.borderRadius.default,
        '& > div': {
            fontWeight: aampeTheme.weights.medium,
        },
    },
}));

const PaginationContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: aampeTheme.spacing(2),
    marginBottom: aampeTheme.spacing4(2),
});

const PaginationText = styled.span({
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.textAccessibleWG01,
    fontWeight: aampeTheme.weights.medium,
});

const PreviewContent = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    width: '100%',
});

interface ScreenSizeProps {
    isMobile: boolean;
}

const ScreenSizeContainer = styled.div<ScreenSizeProps>(({ isMobile }) => ({
    border: `${aampeTheme.colors.bordersWG01} 1px solid`,
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.white,
    padding: aampeTheme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    width: isMobile ? '375px' : '100%',
    maxWidth: isMobile ? '375px' : '800px',
    minHeight: '500px',
    transition: 'width 0.3s ease',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
}));

const SubjectLine = styled.div({
    fontSize: aampeTheme.size.font.textSmall,
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.medium,
    marginBottom: aampeTheme.spacing4(2),
    borderBottom: `1px solid ${aampeTheme.colors.bordersWG01}`,
    paddingBottom: aampeTheme.spacing4(2),
});
